import React from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { AppConfiguration, Message, MessageHandlerDisplayType, PublicRoutes, QueryParamKeys, QueryParamValues } from "src/common";
import { DeveloperErrorMessages, DeveloperWarningMessages, DeveloperSuccessMessages, useCreateAccount, useAddOrgUser } from "src/common/developers";
import { uiActions } from "src/store/ui-manager/slice";
import * as yup from "yup";

function DefaultCreateOrgFormComponent() {
  const default_org_name = "Testing Organization";
  const default_org_domain = "testorg.com";
  const default_user_number = 1;
  const { createUser } = useCreateAccount();
  const { addNewUsers, createNewOrg, success, setSuccess } = useAddOrgUser();
  const dispatch = useDispatch();

  const schema = yup.object().shape({
    org_name: yup.string().required("Organization name is required"),
    org_domain: yup.string().required("Organization domain is required"),
    user_number: yup
      .number()
      .min(1, "Invalid number. Please pick a number between 1 and 30")
      .max(200, "Invalid number. Please pick a number between 1 and 30")
      .required("Number of users is required"),
  });

  function handleError(error: any) {
    switch (error.status) {
      case 404:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_API_error, MessageHandlerDisplayType.toastr)));
        break;
      case 409:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_duplicate_org, MessageHandlerDisplayType.toastr)));
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_creating_org, MessageHandlerDisplayType.toastr)));
    }
  }

  async function handleSubmit(org_name: string, org_domain: string, user_number: number) {
    const isWeb = AppConfiguration.buildForWeb();
    if (isWeb) {
      dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
      return;
    }

    // Note: Create an account for the admin user, use that user to create the organization and add the new users as standard members.
    const admin_id = "admin@" + org_domain;
    try {
      await createUser(admin_id, "Admin User");
      const org_id = await createNewOrg(admin_id, org_name);
      const createUserPromises = [];
      for (let i = 1; i < user_number; i++) {
        const user_id = "standard+" + i.toString() + "@" + org_domain;
        createUserPromises.push(
          addNewUsers(admin_id, org_id, user_id, "Standard User " + i.toString(), "standard", "Dept")
        );
      }
      await Promise.all(createUserPromises);
      setSuccess(true);
      dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_creating_org)));
    } catch (error: any) {
      console.log(error);
      handleError(error);
    }
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{
        org_name: default_org_name,
        org_domain: default_org_domain,
        user_number: default_user_number,
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const org_name = values.org_name;
        const org_domain = values.org_domain;
        const user_number = values.user_number;
        await handleSubmit(org_name, org_domain, user_number);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="org_name">Organization Name</Form.Label>
            <Form.Control
              id="org_name"
              name="org_name"
              type="text"
              placeholder={default_org_name}
              value={values.org_name}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.org_name && touched.org_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.org_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="org_domain">Organization Domain</Form.Label>
            <Form.Control
              id="org_domain"
              name="org_domain"
              type="text"
              placeholder={default_org_domain}
              value={values.org_domain}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.org_domain && touched.org_domain}
            />
            <Form.Control.Feedback type="invalid">
              {errors.org_domain}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_number">Number of Users</Form.Label>
            <Form.Control
              id="user_number"
              name="user_number"
              type="number"
              min={1}
              max={30}
              placeholder={default_user_number.toString()}
              value={values.user_number}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_number && touched.user_number}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_number}
            </Form.Control.Feedback>
          </Form.Group>

          <Alert variant="info" className="mb-3">
            This will create 1 admin user and the remaining will be standard users. <br />
            Admin userID starts with admin@, standard userID starts with standard+1@, standard+2@, etc.
          </Alert>
          <Button type="submit" className="me-2" disabled={isSubmitting}>
            Create Organization
          </Button>
          {success && <Button variant="link" href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}>Choose Account</Button>}
        </Form>
      )}
    </Formik>
  );
}

export default React.memo(DefaultCreateOrgFormComponent);
